import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { notify } from './Notifier';

/**
 * Phải khớp ALLOWED_IMAGE_MIME_TYPES / MAX_IMAGE_BYTES ở apps/api/src/lib/r2.ts.
 * Lệch nhau thì người dùng hoặc bị chặn oan ở client, hoặc upload xong mới ăn 400.
 */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

interface ImageUploaderProps {
  /**
   * Chế độ controlled: cha giữ danh sách file và tự upload khi lưu. Dùng cho
   * form tạo mới (bug, nghiệm thu) vì bản ghi chưa tồn tại lúc chọn ảnh — huỷ
   * form thì không để lại rác trên R2. Khi có `value`, nút "Tải lên" bị ẩn.
   */
  value?: File[];
  onChange?: (files: File[]) => void;
  /**
   * Chế độ uncontrolled: component tự giữ danh sách và hiện nút "Tải lên".
   * Dùng khi bản ghi đã tồn tại (ảnh tính năng ở trang quản trị).
   */
  onUpload?: (files: File[]) => Promise<void> | void;
  /** Cho phép chọn/kéo-thả nhiều file cùng lúc (mặc định true). */
  multiple?: boolean;
  /**
   * Bắt Ctrl+V ở cấp tài liệu để dán ảnh chụp màn hình (mặc định bật). Chỉ nuốt
   * sự kiện khi clipboard CÓ file ảnh, nên dán chữ vào ô ghi chú vẫn bình thường.
   */
  pasteEnabled?: boolean;
  disabled?: boolean;
  /**
   * Thu gọn thành một nút kẹp giấy thay cho vùng kéo-thả to. Dùng ở nơi chỗ
   * đính ảnh chỉ là phụ và không đáng chiếm diện tích (ô bình luận của bug).
   * Dán Ctrl+V vẫn hoạt động như thường; kéo-thả thì thả vào vùng nút/ảnh.
   * Khi kèm `registerOpenPicker`, nút kẹp giấy riêng của component này bị ẩn
   * (xem registerOpenPicker).
   */
  compact?: boolean;
  /**
   * Nhận hàm mở hộp thoại chọn tệp, để nơi dùng đặt được nút "đính kèm" ở chỗ
   * khác (vd nút kẹp ghim trên thanh nhập của chatbot) thay cho nút kẹp giấy
   * mặc định của `compact`. Truyền hàm đã được useCallback bọc, nếu không
   * effect sẽ chạy lại mỗi lần render.
   */
  registerOpenPicker?: (open: () => void) => void;
}

/**
 * Vùng chọn ảnh dùng chung: bấm chọn tệp, kéo-thả, hoặc dán từ clipboard.
 * Kiểm tra định dạng (png/jpg/jpeg/webp) và dung lượng (≤5MB), hiển thị
 * bản xem trước trước khi gửi.
 */
export function ImageUploader({
  value,
  onChange,
  onUpload,
  multiple = true,
  pasteEnabled = true,
  disabled = false,
  compact = false,
  registerOpenPicker,
}: ImageUploaderProps): JSX.Element {
  const isControlled = value !== undefined;
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState<{ file: File; url: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    registerOpenPicker?.(() => inputRef.current?.click());
  }, [registerOpenPicker]);
  const rootRef = useRef<HTMLDivElement>(null);

  const files = isControlled ? value : internalFiles;

  const setFiles = useCallback(
    (next: File[]) => {
      if (isControlled) onChange?.(next);
      else setInternalFiles(next);
    },
    [isControlled, onChange],
  );

  // Mỗi File được cấp 1 objectURL; thu hồi khi danh sách đổi hoặc unmount để
  // không rò rỉ bộ nhớ sau nhiều lần mở/đóng dialog.
  const previews = useMemo(() => files.map((file) => ({ file, url: URL.createObjectURL(file) })), [files]);
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  // `files` đổi tham chiếu mỗi lần render ở chế độ controlled nếu cha không
  // memo hoá; dùng ref để validateAndAdd không phải phụ thuộc vào nó.
  const filesRef = useRef(files);
  filesRef.current = files;

  const validateAndAdd = useCallback(
    (incoming: FileList | File[]) => {
      const accepted: File[] = [];
      let rejected = 0;

      for (const file of Array.from(incoming)) {
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_BYTES) {
          rejected += 1;
          continue;
        }
        accepted.push(file);
      }

      if (rejected > 0) {
        notify(`${rejected} tệp bị bỏ qua (chỉ nhận PNG/JPG/JPEG/WEBP, tối đa 5MB mỗi tệp)`, 'warning');
      }
      if (accepted.length > 0) {
        setFiles(multiple ? [...filesRef.current, ...accepted] : accepted);
      }
    },
    [multiple, setFiles],
  );

  useEffect(() => {
    if (!pasteEnabled || disabled) return;

    // Nghe ở DIALOG gần nhất nếu có, chứ không phải lúc nào cũng document:
    // màn nghiệm thu có sẵn một uploader trên trang, nên khi mở dialog "Thêm
    // bug" mà cả hai cùng nghe document thì ảnh dán vào dialog sẽ lọt luôn
    // sang form nghiệm thu phía sau và bị gửi nhầm.
    const scope: Element | Document = rootRef.current?.closest('[role="dialog"]') ?? document;

    const handlePaste = (e: ClipboardEvent) => {
      // Uploader bên trong dialog xử lý trước (sự kiện nổi từ trong ra ngoài);
      // uploader ngoài trang thấy đã preventDefault thì không nhận nữa.
      if (e.defaultPrevented) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const images: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (file && file.type.startsWith('image/')) images.push(file);
      }

      // Clipboard chỉ có chữ → KHÔNG preventDefault, để ô nhập liệu dán như thường.
      if (images.length === 0) return;
      e.preventDefault();
      validateAndAdd(images);
    };

    scope.addEventListener('paste', handlePaste as EventListener);
    return () => scope.removeEventListener('paste', handlePaste as EventListener);
  }, [pasteEnabled, disabled, validateAndAdd]);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      if (e.dataTransfer.files?.length) validateAndAdd(e.dataTransfer.files);
    },
    [disabled, validateAndAdd],
  );

  const removeAt = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleUploadClick = async () => {
    if (files.length === 0 || !onUpload) return;
    setUploading(true);
    try {
      await onUpload(files);
      setFiles([]);
    } finally {
      setUploading(false);
    }
  };

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPTED_IMAGE_TYPES.join(',')}
      multiple={multiple}
      hidden
      disabled={disabled}
      onChange={(e) => {
        if (e.target.files?.length) validateAndAdd(e.target.files);
        // Reset để chọn lại đúng tệp vừa bỏ vẫn kích hoạt onChange.
        e.target.value = '';
      }}
    />
  );

  // Hai kiểu "compact" khác nhau cho hai nơi dùng khác nhau:
  // - compact đơn thuần (bug comment): nút kẹp giấy tự vẽ, không có dropzone to.
  // - compact + registerOpenPicker (chatbot): vẫn là dropzone (thu nhỏ), vì
  //   nút "đính kèm" thật nằm ở thanh nhập tin nhắn bên ngoài, gọi vào đây qua
  //   registerOpenPicker — ImageUploader không tự vẽ nút nào cả.
  const useIconButtonCompact = compact && !registerOpenPicker;

  return (
    <Box ref={rootRef}>
      {useIconButtonCompact ? (
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <Tooltip title="Đính kèm ảnh (hoặc dán bằng Ctrl+V)">
            <span>
              <IconButton
                data-testid="image-attach-button"
                size="small"
                color={dragOver ? 'primary' : 'default'}
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
              {fileInput}
            </span>
          </Tooltip>
        </Box>
      ) : (
        <Box
          data-testid="image-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 2,
            bgcolor: dragOver ? 'action.hover' : 'transparent',
            p: compact ? 1 : 3,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: 'border-color 0.15s, background-color 0.15s',
          }}
        >
          <CloudUploadIcon color="action" sx={{ fontSize: compact ? 22 : 36, mb: compact ? 0.25 : 1 }} />
          <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary" sx={{ display: 'block' }}>
            Kéo thả, dán, hoặc bấm để chọn ảnh
          </Typography>
          {!compact && (
            <Typography variant="caption" color="text.secondary">
              Hỗ trợ PNG, JPG, JPEG, WEBP — tối đa 5MB mỗi ảnh
            </Typography>
          )}
          {fileInput}
        </Box>
      )}

      {previews.length > 0 && (
        <Box sx={{ mt: compact ? 1 : 2 }}>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={compact && registerOpenPicker ? 0.75 : 1.5}
            data-testid="image-previews"
          >
            {previews.map((p, index) => (
              <Box
                key={`${p.file.name}-${index}`}
                sx={{
                  position: 'relative',
                  width: compact ? (registerOpenPicker ? 48 : 72) : 96,
                  height: compact ? (registerOpenPicker ? 48 : 72) : 96,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="img"
                  src={p.url}
                  alt={p.file.name}
                  onClick={() => setViewing(p)}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
                />
                <IconButton
                  aria-label={`Bỏ ảnh ${p.file.name}`}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(index);
                  }}
                  disabled={uploading || disabled}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    p: compact ? 0 : 0.25,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: compact ? 14 : 18 }} />
                </IconButton>
              </Box>
            ))}
          </Stack>

          {/* Controlled: cha upload lúc lưu nên không có nút riêng ở đây. */}
          {!isControlled && onUpload && (
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => void handleUploadClick()}
              disabled={uploading || disabled}
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            >
              {uploading ? 'Đang tải lên…' : `Tải lên ${files.length} ảnh`}
            </Button>
          )}
        </Box>
      )}

      <Dialog open={viewing !== null} onClose={() => setViewing(null)} maxWidth="lg">
        <IconButton
          aria-label="Đóng"
          onClick={() => setViewing(null)}
          sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}
        >
          <CloseIcon />
        </IconButton>
        {viewing && (
          <Box
            component="img"
            src={viewing.url}
            alt={viewing.file.name}
            sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh' }}
          />
        )}
      </Dialog>
    </Box>
  );
}
